import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, RefreshCw, Download, ShieldCheck, Mail, CheckCircle2 } from "lucide-react";
import { API, formatApiError } from "@/lib/api";

export default function AdminReportPage() {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [busyKey, setBusyKey] = useState("");
  const [exporting, setExporting] = useState(false);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    API.get(`/admin/reports/${reportId}`)
      .then(({ data }) => setReport(data))
      .catch(() => navigate("/admin"));
  }, [reportId, navigate]);

  useEffect(load, [load]);

  const patchSection = async (key, payload) => {
    try {
      await API.patch(`/admin/reports/${reportId}/sections/${key}`, payload);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateLocal = (key, patch) =>
    setReport((r) => ({ ...r, sections: r.sections.map((s) => (s.key === key ? { ...s, ...patch } : s)) }));

  const toggleApproved = (s) => {
    updateLocal(s.key, { approved: !s.approved });
    patchSection(s.key, { approved: !s.approved });
  };

  const regenerate = async (key) => {
    setBusyKey(key);
    try {
      const { data } = await API.post(`/admin/reports/${reportId}/regenerate/${key}`);
      updateLocal(key, { content: data.content, approved: false });
      toast.success("Section redrafted — review and approve it again.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyKey("");
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await API.get(`/admin/reports/${reportId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `digital-audit-${(report.client.business || "client").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF exported.");
    } catch (err) {
      toast.error("Export blocked — approve every section first.");
    } finally {
      setExporting(false);
    }
  };

  const sendToClient = async () => {
    setSending(true);
    try {
      await API.post(`/admin/reports/${reportId}/send`);
      setReport((r) => ({ ...r, sent_at: new Date().toISOString() }));
      toast.success(`Report emailed to ${report.client.email}`);
    } catch (err) {
      toast.error(formatApiError(err, "Send failed — check the email balance and try again."));
    } finally {
      setSending(false);
    }
  };

  if (!report) {
    return (
      <main className="flex min-h-screen items-center justify-center" data-testid="report-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </main>
    );
  }

  const aiSections = report.sections.filter((s) => s.ai);
  const approvedCount = aiSections.filter((s) => s.approved).length;
  const allApproved = approvedCount === aiSections.length;

  return (
    <main className="mx-auto max-w-7xl px-6 pt-32 pb-24 lg:px-10" data-testid="report-editor">
      <button onClick={() => navigate("/admin")} data-testid="report-back-btn" className="flex items-center gap-2 text-xs text-slate-400 transition-colors duration-300 hover:text-gold-glow">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </button>

      <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="eyebrow">Audit Report — internal draft</p>
          <h1 className="mt-2 font-serif text-3xl text-ivory" data-testid="report-client-name">
            {report.client.business || report.client.name || "Client"}
          </h1>
          <p className="mt-1 font-mono text-xs text-slate-500">
            {approvedCount}/{aiSections.length} sections approved
            {report.sent_at && <span className="ml-3 text-emerald-400">· Emailed to client {new Date(report.sent_at).toLocaleDateString()}</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="h-1.5 w-40 overflow-hidden rounded-full bg-navy-surface" data-testid="report-progress">
            <div className="h-full bg-gold transition-all duration-500" style={{ width: `${(approvedCount / aiSections.length) * 100}%` }} />
          </div>
          <button
            onClick={exportPdf}
            disabled={!allApproved || exporting}
            data-testid="report-export-btn"
            className="flex items-center gap-2 rounded-full border border-gold/40 px-6 py-3 text-xs font-semibold text-gold-glow transition-colors duration-300 hover:bg-gold/10 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4" /> Export PDF</>}
          </button>
          <button
            onClick={sendToClient}
            disabled={!allApproved || sending}
            data-testid="report-send-btn"
            className="flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-xs font-semibold text-navy-deep transition-colors duration-300 hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-40"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : report.sent_at ? <><CheckCircle2 className="h-4 w-4" /> Resend to client</> : <><Mail className="h-4 w-4" /> Email to client</>}
          </button>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 rounded-2xl border border-gold/25 bg-gold/5 p-5">
        <ShieldCheck className="h-5 w-5 shrink-0 text-gold" />
        <p className="text-xs leading-relaxed text-slate-300">
          Client answers are private to you and are used only to prepare this report. Every AI-drafted section stays
          marked DRAFT until you approve it, and the PDF cannot be exported or emailed until all sections are approved.
        </p>
      </div>

      <div className="mt-10 grid gap-10 lg:grid-cols-12">
        <aside className="lg:col-span-4">
          <div className="gold-ring-card sticky top-28 p-7" data-testid="report-client-answers">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold/80">Client answers</p>
            <dl className="mt-5 space-y-4">
              {Object.entries(report.client.answers || {}).map(([k, v]) => (
                <div key={k}>
                  <dt className="text-[10px] uppercase tracking-widest text-slate-500">{k.replace(/_/g, " ")}</dt>
                  <dd className="mt-1 text-sm font-light text-slate-200">{v}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-6 border-t border-gold/10 pt-4 text-xs text-slate-500">{report.client.email}</p>
          </div>
        </aside>

        <div className="space-y-6 lg:col-span-8" data-testid="report-sections">
          {report.sections.map((s, i) => (
            <section key={s.key} className={`gold-ring-card p-7 ${s.ai && !s.approved ? "border-gold/40" : ""}`} data-testid={`report-section-${s.key}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-serif text-xl text-ivory">{i + 1}. {s.title}</h2>
                <div className="flex items-center gap-3">
                  {s.ai && (
                    <span
                      data-testid={`report-status-${s.key}`}
                      className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${s.approved ? "bg-emerald-500/15 text-emerald-400" : "bg-gold/15 text-gold-light"}`}
                    >
                      {s.approved ? "Approved" : "DRAFT: review before sending"}
                    </span>
                  )}
                  {!s.ai && <span className="rounded-full bg-navy-light px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">Fixed content</span>}
                </div>
              </div>
              <textarea
                value={s.content}
                onChange={(e) => updateLocal(s.key, { content: e.target.value, approved: s.ai ? false : s.approved })}
                onBlur={(e) => patchSection(s.key, { content: e.target.value })}
                rows={Math.min(14, Math.max(5, s.content.split("\n").length + 1))}
                className="input-lux mt-5 resize-y font-mono text-xs leading-relaxed"
                data-testid={`report-text-${s.key}`}
              />
              {s.ai && (
                <div className="mt-4 flex items-center justify-between">
                  <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-300">
                    <input
                      type="checkbox"
                      checked={s.approved}
                      onChange={() => toggleApproved(s)}
                      data-testid={`report-approve-${s.key}`}
                      className="h-4 w-4 accent-[#C9A227]"
                    />
                    I have reviewed this section — approved
                  </label>
                  <button
                    onClick={() => regenerate(s.key)}
                    disabled={busyKey === s.key}
                    data-testid={`report-regenerate-${s.key}`}
                    className="flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 text-[11px] text-gold-glow transition-colors duration-300 hover:bg-gold/10 disabled:opacity-50"
                  >
                    {busyKey === s.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    Redraft with AI
                  </button>
                </div>
              )}
            </section>
          ))}
        </div>
      </div>
    </main>
  );
}
